import type {Module} from "vuex";
import type {RootState} from "@/store/types";
import {get} from "lodash"
import {platform} from "@/api/platform";
import emitter from "@/api/events";

// Cart item; it is identified by the property named in state.id ("sku")
export interface CartItem {
    quantity: number
    [key: string]: unknown
}

export interface Cart {
    items?: CartItem[]
    version: string | null
    properties: Record<string, unknown>
}

export interface CartSetItemQuantityPayload {
    quantity: number
    [key: string]: unknown
}

export interface CartRefreshOptions {
    start?: boolean
    interval?: number
}

export interface CartState {
    id: string
    loading: boolean
    items: CartItem[]
    version: string | null
    properties: Record<string, unknown>
}

let REFRESH_INTERVAL: ReturnType<typeof setInterval> | undefined;

const cart: Module<CartState, RootState> = {
    namespaced: true,
    state: {
        id: "sku",
        loading: false,
        items: [],
        version: null,
        properties: {}
    },
    getters: {
        id: (state): string => state.id,
        loading: (state): boolean => state.loading,
        items: (state): CartItem[] => state.items,
        total: (state): number => state.items.reduce((total, item) => total + item.quantity, 0),
        version: (state): string | null => state.version,
        properties: (state): Record<string, unknown> => state.properties,
        exists: state => (item: Record<string, unknown>): boolean => -1 !== state.items.findIndex(i => i[state.id] === item[state.id])
    },
    mutations: {
        setLoading: (state, loading: boolean) => state.loading = loading,
        setItems: (state, items: CartItem[]) => state.items = items,
        setVersion: (state, version: string | null) => state.version = version,
        setProperties: (state, properties: Record<string, unknown>) => state.properties = properties,
        setCart(state, cart: Cart) {

            state.items.splice(0, state.items.length, ...get(cart, "items", []));
            state.version = cart.version
            state.properties = cart.properties;
        }
    },
    actions: {
        fetch(context) {

            context.commit("setLoading", true);

            return platform
                .get<{results: Cart}>(`/api/v2/users/${this.getters["user/id"]}/cart`)
                .then(response => context.commit("setCart", response.data.results))
                .finally(() => context.commit("setLoading", false));
        },
        addItem(context, id: unknown) {

            context.commit("setLoading", true);

            return platform
                .patch<{results: Cart}>(`/api/v2/users/${this.getters["user/id"]}/cart`, {
                    action: "add",
                    [context.getters["id"]]: id,
                    quantity: 1
                })
                .then(response => context.dispatch("setCart", response.data.results))
                .finally(() => context.commit("setLoading", false));
        },
        setItemQuantity(context, data: CartSetItemQuantityPayload) {

            context.commit("setLoading", true);

            const item = context.getters["items"].find((item: CartItem) => item[context.getters["id"]] === data[context.getters["id"]]);
            const action = data.quantity > item.quantity ? "add" : "remove";
            const quantity = data.quantity > item.quantity
                ? data.quantity - item.quantity
                : item.quantity - data.quantity

            return platform
                .patch<{results: Cart}>(`/api/v2/users/${this.getters["user/id"]}/cart`, {
                    action,
                    [context.getters["id"]]: data[context.getters["id"]],
                    quantity,
                })
                .then(response => context.dispatch("setCart", response.data.results))
                .finally(() => context.commit("setLoading", false));
        },
        removeItem(context, id: unknown) {

            return platform
                .patch<{results: Cart}>(`/api/v2/users/${this.getters["user/id"]}/cart`, {
                    action: "remove",
                    [context.getters["id"]]: id,
                    quantity: context.getters["items"].find((item: CartItem) => item[context.getters["id"]] === id).quantity || 1,
                })
                .then(response => context.dispatch("setCart", response.data.results))
                .finally(() => context.commit("setLoading", false));
        },
        setCart(context, items: Cart) {

            emitter.emit('user_cart_change');
            context.commit("setCart", items);
        },
        // Starts (start + interval) or stops re-fetching the cart in the background
        refresh(context, options?: CartRefreshOptions): boolean {

            if (options && options.start && options.interval) {

                clearInterval(REFRESH_INTERVAL);
                REFRESH_INTERVAL = setInterval(
                    () => platform
                        .get<{results: Cart}>(`/api/v2/users/${this.getters["user/id"]}/cart`)
                        .then(response => context.commit("setCart", response.data.results)),
                    options.interval
                );

                return true;
            }

            clearInterval(REFRESH_INTERVAL);

            return false;
        },
        clear(context) {

            context.commit("setItems", []);
            context.commit("setVersion", process.env.VUE_APP_CART_VERSION);
            context.commit("setProperties", {});
        }
    },
    modules: {}
}

export default cart
